import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { IconFilter, IconRefresh, IconCalendar } from '@tabler/icons-react';

import SuccessRateCard from './SuccessRateCard';
import PlanDrugCombosTable from './PlanDrugCombosTable';
import TabEngagementChart from './TabEngagementChart';
import CoverageOutcomesChart from './CoverageOutcomesChart';
import TherapeuticClassChart from './TherapeuticClassChart';
import PBMPayerPerformance from './PBMPayerPerformance';
import superAdminApiService from '../../../services/super-admin-api.service';

const NewCoverageVerificationAnalytics = () => {
  const [dateRange, setDateRange] = useState('30d');
  const [selectedOrg, setSelectedOrg] = useState('all');
  const [organizations, setOrganizations] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgError, setOrgError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const dateRangeOptions = [
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '60d', label: 'Last 60 Days' },
    { value: '90d', label: 'Last 90 Days' },
  ];

  useEffect(() => {
    const fetchOrganizations = async () => {
      setOrgError(false);
      setOrgLoading(true);

      try {
        const data = await superAdminApiService.listOrganizationUsers();
        const filteredData = data.results.map((organization) => ({
          id: organization?.organization_id,
          name: organization?.org_name,
        }));
        setOrganizations([
          { id: 'all', name: 'All Organizations' },
          ...filteredData,
        ]);
      } catch (err) {
        console.error('Failed to load organizations:', err);
        setOrgError('Failed to load organizations');
      } finally {
        setOrgLoading(false);
      }
    };

    fetchOrganizations();
  }, []);

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const getFilterSummary = () => {
    const parts = [];

    const rangeLabel = dateRangeOptions.find((opt) => opt.value === dateRange)?.label;
    if (rangeLabel) {
      parts.push(`Period: ${rangeLabel}`);
    }

    if (selectedOrg !== 'all') {
      const orgName = organizations.find((o) => o.id === selectedOrg)?.name;
      parts.push(`Organization: ${orgName}`);
    }

    return parts.length ? parts.join(' | ') : 'All Data';
  };

  const filterProps = {
    organization: selectedOrg === 'all' ? undefined : selectedOrg,
    dateRange,
    refreshKey,
  };

  return (
    <div className="innerpage detailed-page">
      <div className="w-full">
        {/* Header */}
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-0">
            New Coverage Verification Analytics
          </h4>
          <p className="text-muted-foreground text-sm mb-0">
            Comprehensive coverage check performance and user engagement metrics
          </p>
        </div>

        {/* Filters */}
        <Card className="shadow-sm border-0 mb-6">
          <CardHeader className="pb-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <IconFilter size={18} />
                <CardTitle className="text-base font-semibold mb-0">Filters</CardTitle>
                <span className="text-sm text-muted-foreground">
                  {getFilterSummary()}
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                className="flex items-center gap-1"
              >
                <IconRefresh size={16} />
                Refresh
              </Button>
            </div>
          </CardHeader>

          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Date Range */}
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-1">
                  <IconCalendar size={16} />
                  Time Period
                </label>
                <Select value={dateRange} onValueChange={setDateRange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select time period" />
                  </SelectTrigger>
                  <SelectContent>
                    {dateRangeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Organization Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Organization
                </label>
                <Select 
                  value={selectedOrg} 
                  onValueChange={setSelectedOrg}
                  disabled={orgLoading}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {orgLoading ? (
                      <SelectItem value="loading" disabled>Loading...</SelectItem>
                    ) : orgError ? (
                      <SelectItem value="error" disabled>{orgError}</SelectItem>
                    ) : (
                      organizations.map((org) => (
                        <SelectItem key={org.id} value={org.id}>
                          {org.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Success Rate Overview */}
        <div className="mb-6">
          <SuccessRateCard {...filterProps} />
        </div>

        {/* Coverage Outcomes Distribution */}
        <div className="mb-6">
          <CoverageOutcomesChart {...filterProps} />
        </div>

        {/* Tab Engagement */}
        <div className="mb-6">
          <TabEngagementChart {...filterProps} />
        </div>

        {/* Plan-Drug Combinations Table */}
        <div className="mb-6">
          <PlanDrugCombosTable {...filterProps} />
        </div>

        {/* Therapeutic Class Analysis */}
        <div className="mb-6">
          <TherapeuticClassChart {...filterProps} />
        </div>

        {/* PBM & Payer Performance */}
        <div className="mb-6">
          <PBMPayerPerformance {...filterProps} />
        </div>
      </div>
    </div>
  );
};

export default NewCoverageVerificationAnalytics;
